import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { Button, Chip, Banner, Field } from '../components/ui.jsx';
import * as machine from '../lib/till.js';

const card = {
  background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)',
  borderRadius: 'var(--radius-lg)', overflow: 'hidden'
};

const Head = ({ children }) => (
  <div style={{ padding: '13px 16px', background: 'var(--color-bg-subtle)' }} className="t-label-xs muted">
    {children}
  </div>
);

const when = t => t
  ? new Date(t).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  : 'never';

/**
 * The tills, and the printer on each one.
 *
 * This screen exists because of one number nobody can look up. The cash drawer is opened by
 * ESC p m t1 t2: a pin and two timings, and the right values differ between units. Wrong pin
 * and the drawer never opens; wrong timing and the solenoid buzzes or it half opens. There is
 * no way to know except to stand at the machine, change a number, press Test, and look.
 *
 * So everything here is built around that loop being fast.
 */
export function Tills() {
  const [tills, setTills] = useState([]);
  const [transports, setTransports] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [platform, setPlatform] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [adding, setAdding] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [here, setHere] = useState(null);

  const load = () => api.tills.list()
    .then(d => { setTills(d.tills); setLoading(false); })
    .catch(e => { setError(readable(e)); setLoading(false); });

  const loadHere = () => machine.localSettings().then(setHere).catch(() => {});

  useEffect(() => {
    load();
    api.tills.transports().then(d => {
      setTransports(d.transports); setDefaults(d.defaults);
      // Which platform matters is the one holding the printer cable. Off a till that is the
      // server, and the server is a VPS in a rack that will never have a printer plugged in.
      setPlatform(d.platform);
    }).catch(() => {});
    api.capabilities().then(d => setBusinesses(d.businesses)).catch(() => {});
    if (machine.onTill) {
      machine.version().then(v => setPlatform(v.platform)).catch(() => {});
      loadHere();
    }
  }, []);

  if (loading) return <div className="t-body-m muted">Loading the tills</div>;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }} className="t-body-m secondary">
          Every machine that sells, and the printer wired to it. Pairing hands a till a device
          key so it can push sales it took while the connection was down.
        </div>
        <Button onClick={() => setAdding(true)}>Register a till</Button>
      </div>

      {error && <Banner tone="danger">{error}</Banner>}
      {note && <Banner tone="success">{note}</Banner>}

      {machine.onTill && <ThisMachine here={here} onChanged={() => { loadHere(); load(); }} onNote={setNote} />}

      {!tills.length && (
        <div style={{ ...card, padding: 40, textAlign: 'center' }} className="t-body-m muted">
          No tills yet. Register one for the counter it stands on, then pair it from that machine.
        </div>
      )}

      {tills.map(t => (
        <Till key={t.id} till={t} transports={transports} defaults={defaults} platform={platform}
          isThisMachine={machine.onTill && here?.tillCode === t.code}
          onChanged={() => { load(); loadHere(); }} onNote={setNote} onError={setError} />
      ))}

      {adding && (
        <AddTill businesses={businesses} onClose={() => setAdding(false)}
          onAdded={t => { setAdding(false); load(); setNote(`Till ${t.code} registered. Pair it from the machine itself.`); }} />
      )}
    </div>
  );
}

const ERRORS = {
  code_taken: 'A till with that code already exists.',
  code_and_name_required: 'A code and a name are both needed.',
  business_has_no_branch: 'That business has no branch to attach a till to.',
  no_printer_configured: 'No printer address is set on this till yet.',
  bad_printer_settings: 'Those printer settings will not work.',
  forbidden: 'Only a super admin can change the tills.'
};
const readable = e => ERRORS[e?.data?.error] || e?.data?.error || 'That did not work.';

// ---------------------------------------------------------------- the machine you are on

const PAIR_ERRORS = {
  bad_code: 'That code is not right. Ask for it again.',
  code_expired: 'That code has run out. Start pairing again on the server.',
  code_required: 'Type the code first.',
  till_disabled: 'That till has been switched off. A super admin has to turn it back on.',
  unreachable: 'This machine cannot reach EPMS, so it cannot pair yet.'
};

/**
 * Only appears inside the desktop app, and only there because pairing has to happen at the
 * machine. A super admin starts it from anywhere and reads out a code; whoever is standing at
 * the till types it here and the machine gets its key. The key itself never comes into this
 * page: the exchange happens in the desktop shell.
 */
function ThisMachine({ here, onChanged, onNote }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!here) return null;

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    const r = await machine.pair(code.trim());
    setBusy(false);
    if (!r.paired) return setError(PAIR_ERRORS[r.error] || `Pairing failed: ${r.error}`);
    setCode('');
    onNote(`This machine is now till ${r.till.code}, ${r.till.name}.`);
    onChanged();
  }

  return (
    <div style={{ ...card, borderColor: 'var(--color-accent-default)' }}>
      <Head>THIS MACHINE</Head>
      <div style={{ padding: '14px 16px', display: 'grid', gap: 12 }}>
        {here.paired ? (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip tone="success">Paired as {here.tillCode}</Chip>
            <span className="t-body-m">{here.tillName}</span>
            <span className="t-body-s muted">
              Receipts print here, and the drawer opens here. Server: {here.serverUrl}
            </span>
          </div>
        ) : (
          <>
            <div className="t-body-s muted">
              This machine is not a till yet. Ask a super admin to start pairing for the till
              that stands on this counter, then type the code they read out.
            </div>
            <form onSubmit={submit} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div style={{ width: 220 }}>
                <Field id="pairing-code" label="PAIRING CODE" value={code} autoFocus
                  onChange={e => setCode(e.target.value.toUpperCase())} />
              </div>
              <Button type="submit" size="m" disabled={busy || !code.trim()}>
                {busy ? 'Pairing' : 'Pair this machine'}
              </Button>
            </form>
          </>
        )}
        {error && <Banner tone="danger">{error}</Banner>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- one till

function Till({ till, transports, defaults, platform, isThisMachine, onChanged, onNote, onError }) {
  const [open, setOpen] = useState(false);
  const [pairing, setPairing] = useState(null);
  const [settings, setSettings] = useState({ ...defaults, ...(till.settings || {}) });
  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState(null);

  const set = patch => setSettings(s => ({ ...s, ...patch }));
  const transport = transports.find(t => t.name === settings.transport) || transports[0];

  async function run(what, fn) {
    setBusy(what); setResult(null); onError(null);
    try {
      const r = await fn();
      setResult({ ok: true, ...r });
    } catch (e) {
      // The printer's own words, not a generic failure. "Nothing is listening on
      // 192.168.1.50:9100" tells somebody what to do; "print failed" does not.
      setResult({ ok: false, message: e.data?.error || readable(e), problems: e.data?.problems });
    } finally { setBusy(null); }
  }

  /*
   * Where the bytes come from.
   *
   * The server can only reach a printer it shares a network with, and it does not: it is a
   * VPS in a rack, and the printer is on a counter in Oyarifa. So when EPMS is running on a
   * till, the till prints. Everywhere else the server tries, which is still right for a
   * printer that is genuinely on the same network as the server.
   */
  const local = machine.onTill;

  const printed = r => r.sent ? r : Promise.reject({ data: { error: r.error } });

  // A test that worked proves the printer, not that the till will use it. Until Save is
  // pressed nothing is written, and the next real sale finds no printer at all.
  const [savedHere, setSavedHere] = useState(null);
  const [tested, setTested] = useState(false);

  useEffect(() => {
    if (local) machine.localSettings().then(c => setSavedHere(c?.printer?.target || null)).catch(() => {});
  }, [local, busy]);

  const unsaved = local && tested && settings.target && settings.target !== savedHere;

  const test = () => run('test', async () => {
    const r = local ? await machine.testPrint(settings).then(printed) : await api.tills.testPrint(till.id, settings);
    setTested(true);
    return r;
  });

  const drawer = () => run('drawer', () => local
    ? machine.openDrawer(settings).then(printed)
    : api.tills.openDrawer(till.id, settings));

  const save = () => run('save', async () => {
    await api.tills.saveSettings(till.id, settings);
    /*
     * The machine keeps its own copy, because it has to go on printing when the server is
     * unreachable, which is exactly when nobody can look these values up.
     *
     * This used to happen only when the till was paired, and that was the wrong condition.
     * You set a printer up before you pair, the Test button uses whatever is on the screen,
     * so the test printed, Save said saved, and then the first real sale reported no printer
     * configured. Whichever till row is open, the machine you are standing at is the machine
     * that prints, so on a till it is always written here.
     */
    if (local) await machine.saveLocalSettings({ printer: settings });
    onChanged();
    onNote(`Printer settings saved on ${till.code}${local ? ', on this machine and on the server' : ''}.`);
    return { saved: true };
  });

  return (
    <div style={card}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '14px 16px' }}>
        <div style={{ width: 220, flex: 'none' }}>
          <div className="t-heading-s">{till.name}</div>
          <div className="t-body-s muted">{till.code} · {till.business_name}</div>
        </div>

        <div style={{ display: 'flex', gap: 6, flex: 1, flexWrap: 'wrap' }}>
          {till.paired
            ? <Chip tone="success">Paired {when(till.paired_at)}</Chip>
            : <Chip tone="warning">Not paired</Chip>}
          {till.settings?.target
            ? <Chip showDot={false}>{till.settings.transport}: {till.settings.target}</Chip>
            : <Chip tone="warning">No printer set</Chip>}
          {till.gaps && !till.gaps.complete && (
            <Chip tone="danger">
              {till.gaps.missing.length} sale{till.gaps.missing.length === 1 ? '' : 's'} never arrived
            </Chip>
          )}
        </div>

        <span className="t-body-s muted" style={{ width: 150, textAlign: 'right' }}>
          seen {when(till.last_seen)}
        </span>
        <Button size="m" variant="ghost" style={{ border: '1px solid var(--color-border-default)' }}
          onClick={() => setOpen(!open)}>{open ? 'Close' : 'Set up'}</Button>
      </div>

      {open && (
        <div style={{ borderTop: '1px solid var(--color-border-default)', background: 'var(--color-bg-subtle)' }}>

          {/* ---- pairing */}
          <Head>PAIRING</Head>
          <div style={{ padding: '12px 16px', display: 'grid', gap: 10 }}>
            {pairing ? (
              <Banner tone="info" title="Read this to whoever is at the machine">
                <div className="money t-money-l" style={{ margin: '6px 0' }}>{pairing.code}</div>
                Good for {pairing.minutes} minutes, and it dies the moment it is used. On the
                till, open EPMS and enter it when asked.
              </Banner>
            ) : (
              <div className="t-body-s muted">
                {till.paired
                  ? 'This till holds a device key and can push sales. Pair again only if it was reinstalled.'
                  : 'Not paired. It cannot push anything until it is.'}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <Button size="m" variant="secondary"
                onClick={() => run('pair', async () => {
                  const p = await api.tills.pair(till.id);
                  setPairing(p);
                  onChanged();
                  return { paired: true };
                })}>
                {busy === 'pair' ? 'Working' : till.paired ? 'Pair again' : 'Start pairing'}
              </Button>
              {till.paired && (
                <Button size="m" variant="ghost" style={{ border: '1px solid var(--color-border-default)' }}
                  onClick={() => run('revoke', async () => {
                    await api.tills.revoke(till.id);
                    onChanged();
                    onNote(`${till.code} revoked. It keeps selling offline and can push nothing.`);
                    return { revoked: true };
                  })}>Revoke</Button>
              )}
            </div>
          </div>

          {/* ---- the printer */}
          <Head>THE PRINTER</Head>
          <div style={{ padding: '12px 16px', display: 'grid', gap: 12 }}>
            <div style={{ display: 'grid', gap: 8 }}>
              {transports.map(t => (
                <label key={t.name} style={{
                  display: 'flex', gap: 10, alignItems: 'flex-start', padding: 10, cursor: 'pointer',
                  border: '1px solid ' + (settings.transport === t.name ? 'var(--color-accent-default)' : 'var(--color-border-default)'),
                  background: settings.transport === t.name ? 'var(--color-accent-subtle)' : 'var(--color-bg-surface)',
                  borderRadius: 'var(--radius-md)',
                  opacity: t.name === 'windows' && platform !== 'win32' ? 0.55 : 1
                }}>
                  <input type="radio" name={`transport-${till.id}`} checked={settings.transport === t.name}
                    onChange={() => set({ transport: t.name })} style={{ marginTop: 3 }} />
                  <span>
                    <span className="t-body-m">{t.label}</span>
                    <span className="t-body-s muted" style={{ display: 'block' }}>
                      {t.name === 'windows' && platform !== 'win32'
                        ? 'Only works on the machine the printer is plugged into, and that is not this one.'
                        : t.hint}
                    </span>
                  </span>
                </label>
              ))}
            </div>

            <Field id={`target-${till.id}`} label={(transport?.field || 'TARGET').toUpperCase()}
              value={settings.target || ''}
              placeholder={settings.transport === 'network' ? '192.168.1.50' :
                           settings.transport === 'windows' ? 'PremaxRP80' : 'auto'}
              onChange={e => set({ target: e.target.value })} />
          </div>

          {/* ---- the drawer, which is the reason this screen exists */}
          <Head>THE CASH DRAWER</Head>
          <div style={{ padding: '12px 16px', display: 'grid', gap: 12 }}>
            <div className="t-body-s muted">
              The drawer hangs off the printer, not the computer, and is opened by a pulse.
              Which pin and how long differ between units, so there is nothing to look up:
              change a value, press Test, and see whether it opens.
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ width: 150 }}>
                <div className="t-label-xs muted" style={{ marginBottom: 4 }}>PIN</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[0, 1].map(pin => (
                    <button key={pin} onClick={() => set({ drawerPin: pin })}
                      style={{
                        flex: 1, padding: '10px 0', cursor: 'pointer', font: 'inherit',
                        border: '1px solid ' + (settings.drawerPin === pin ? 'var(--color-accent-default)' : 'var(--color-border-default)'),
                        background: settings.drawerPin === pin ? 'var(--color-accent-subtle)' : 'var(--color-bg-surface)',
                        borderRadius: 'var(--radius-md)'
                      }}>{pin}</button>
                  ))}
                </div>
              </div>
              <div style={{ width: 160 }}>
                <Field id={`on-${till.id}`} label="PULSE ON, MS" value={settings.drawerOnMs ?? 50}
                  inputMode="numeric" onChange={e => set({ drawerOnMs: Number(e.target.value) || 0 })} />
              </div>
              <div style={{ width: 160 }}>
                <Field id={`off-${till.id}`} label="PULSE OFF, MS" value={settings.drawerOffMs ?? 200}
                  inputMode="numeric" onChange={e => set({ drawerOffMs: Number(e.target.value) || 0 })} />
              </div>
            </div>
          </div>

          {/* ---- paper */}
          <Head>PAPER</Head>
          <div style={{ padding: '12px 16px', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ width: 200 }}>
              <div className="t-label-xs muted" style={{ marginBottom: 4 }}>CUT</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {['partial', 'full'].map(c => (
                  <button key={c} onClick={() => set({ cut: c })}
                    style={{
                      flex: 1, padding: '10px 0', cursor: 'pointer', font: 'inherit', textTransform: 'capitalize',
                      border: '1px solid ' + (settings.cut === c ? 'var(--color-accent-default)' : 'var(--color-border-default)'),
                      background: settings.cut === c ? 'var(--color-accent-subtle)' : 'var(--color-bg-surface)',
                      borderRadius: 'var(--radius-md)'
                    }}>{c}</button>
                ))}
              </div>
            </div>
            <div style={{ width: 200 }}>
              <Field id={`feed-${till.id}`} label="FEED BEFORE CUTTING, LINES"
                value={settings.feedBeforeCut ?? 4} inputMode="numeric"
                hint="Too few and the cutter takes the last line off."
                onChange={e => set({ feedBeforeCut: Number(e.target.value) || 0 })} />
            </div>
          </div>

          {/* ---- do it */}
          <div style={{ padding: 16, display: 'grid', gap: 12, borderTop: '1px solid var(--color-border-default)' }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button size="m" onClick={test} disabled={busy === 'test'}>
                {busy === 'test' ? 'Printing' : 'Print a test page'}
              </Button>
              <Button size="m" variant="secondary" onClick={drawer} disabled={busy === 'drawer'}>
                {busy === 'drawer' ? 'Opening' : 'Open the drawer only'}
              </Button>
              {local && (
                <span className="t-body-s muted" style={{ alignSelf: 'center' }}>
                  from this machine
                </span>
              )}
              <span style={{ flex: 1 }} />
              <Button size="m" variant="secondary" onClick={save} disabled={busy === 'save'}>
                {busy === 'save' ? 'Saving' : 'Save these settings'}
              </Button>
            </div>

            {unsaved && (
              <Banner tone="warning" title="It printed, but nothing is saved yet">
                The test used what is on this screen. Until you press Save these settings, this
                machine still has no printer, and a completed sale will not print by itself.
              </Banner>
            )}

            {result && (result.ok
              ? <Banner tone="success" title={result.saved ? 'Saved' : result.revoked ? 'Revoked' : 'Sent'}>
                  {result.saved ? 'These settings are now what this till prints with.'
                    : result.paired ? 'The code is above. It dies when it is used.'
                    : result.revoked ? 'The device key is dead. Pair again to bring it back.'
                    : `${result.bytes} bytes over ${result.transport} to ${result.target}, in ${result.ms}ms. `
                      + 'Look at the paper: the ruler line should end on an 8. If it does not, the layout is wider than the head can reach.'}
                </Banner>
              : <Banner tone="danger" title="It did not print">
                  {result.message}
                  {result.problems?.length > 0 && (
                    <ul style={{ margin: '6px 0 0 18px' }}>
                      {result.problems.map((p, i) => <li key={i}>{p}</li>)}
                    </ul>
                  )}
                </Banner>)}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- registering one

function AddTill({ businesses, onClose, onAdded }) {
  const [form, setForm] = useState({ businessSlug: businesses[0]?.slug || '', code: '', name: '' });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = p => setForm(f => ({ ...f, ...p }));

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try { onAdded((await api.tills.create(form)).till); }
    catch (err) { setError(readable(err)); setBusy(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'grid', placeItems: 'center', padding: 24, background: 'rgba(2,6,23,0.45)' }}>
      <form className="card" onSubmit={submit} style={{ width: 460, padding: 24, display: 'grid', gap: 16 }}>
        <div className="t-heading-l">Register a till</div>
        <div className="t-body-s muted">
          One per counter. The code becomes part of every reference it prints, so it is worth
          keeping short and obvious: T1, T2, BAR.
        </div>

        <div className="field">
          <label className="t-label-xs">BUSINESS</label>
          <select value={form.businessSlug} onChange={e => set({ businessSlug: e.target.value })}
            style={{
              height: 48, padding: '0 12px', borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-border-strong)', background: 'var(--color-bg-surface)',
              color: 'var(--color-text-primary)', font: '400 15px/22px var(--font-ui)'
            }}>
            {businesses.map(b => <option key={b.slug} value={b.slug}>{b.short_name}</option>)}
          </select>
        </div>

        <Field id="tillcode" label="CODE" value={form.code} autoFocus
          hint="Goes into every reference: SPA-T1-000142."
          onChange={e => set({ code: e.target.value.toUpperCase() })} />
        <Field id="tillname" label="NAME" value={form.name}
          hint="Where it stands. Front counter, back room."
          onChange={e => set({ name: e.target.value })} />

        {error && <Banner tone="danger">{error}</Banner>}

        <div style={{ display: 'flex', gap: 8 }}>
          <Button type="button" variant="ghost" style={{ border: '1px solid var(--color-border-default)' }}
            onClick={onClose}>Cancel</Button>
          <Button type="submit" style={{ flex: 1 }} disabled={busy || !form.code.trim() || !form.name.trim()}>
            {busy ? 'Registering' : 'Register it'}
          </Button>
        </div>
      </form>
    </div>
  );
}
